import { useState } from "react";

import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { queryClient } from "@impero-com/common/utils/query_client";
import { emitNotification, TextToolState } from "@impero-com/ui";
import { Row } from "@tanstack/react-table";

import {
    postControlResultsUnarchive,
    getGetControlResultsInfiniteQueryKey,
} from "api/generated/control-results/control-results";

import { ControlActivityTableRow } from "./types";

function computeUnarchiveToolState(
    t: TFunction,
    hasSelection: boolean,
    hasActiveSelection: boolean,
    hasPermission: boolean
): TextToolState {
    if (!hasSelection) {
        return {
            type: "DISABLED",
            reason: t("ControlActivitiesList_Tooltip_NoSelection"),
        };
    }
    if (hasActiveSelection) {
        return {
            type: "DISABLED",
            reason: t("ControlActivitiesList_Unarchive_Tooltip_Conflict"),
        };
    }
    if (!hasPermission) {
        return {
            type: "DISABLED",
            reason: t("ControlActivitiesList_Tooltip_Unarchive_NoPermission"),
        };
    }
    return { type: "ENABLED" };
}

interface UseControlActivityUnarchiveUtilsProps {
    selectedRows: Row<ControlActivityTableRow>[];
    deselectAll: () => void;
}

export function useControlActivityUnarchiveUtils({
    selectedRows,
    deselectAll,
}: UseControlActivityUnarchiveUtilsProps) {
    const { t } = useTranslation();
    const [unarchiveModalOpen, setUnarchiveModalOpen] = useState(false);
    const [isUnarchiving, setIsUnarchiving] = useState(false);

    const unarchivableIds = selectedRows
        .filter((row) => row.original.archived && row.original.canEditProgram)
        .map((row) => row.original.activityId);

    const hasSelection = selectedRows.length > 0;
    const hasActiveSelection = selectedRows.some(
        (row) => !row.original.archived
    );
    const hasPermission = unarchivableIds.length > 0;

    const unarchiveToolState = computeUnarchiveToolState(
        t,
        hasSelection,
        hasActiveSelection,
        hasPermission
    );

    const openUnarchiveModal = () => setUnarchiveModalOpen(true);
    const closeUnarchiveModal = () => setUnarchiveModalOpen(false);

    const handleUnarchive = async (trimmedComment: string) => {
        setIsUnarchiving(true);
        try {
            await postControlResultsUnarchive({
                controlResultIds: unarchivableIds,
                comment: trimmedComment || undefined,
            });
            await queryClient.invalidateQueries({
                queryKey: getGetControlResultsInfiniteQueryKey(),
            });
            emitNotification(
                "success",
                t("ControlActivitiesList_Unarchive_Success", {
                    count: unarchivableIds.length,
                })
            );
            deselectAll();
            closeUnarchiveModal();
        } catch {
            emitNotification("error", t("UnknownError"));
        } finally {
            setIsUnarchiving(false);
        }
    };

    return {
        unarchiveToolState,
        unarchiveModalOpen,
        openUnarchiveModal,
        closeUnarchiveModal,
        handleUnarchive,
        isUnarchiving,
    };
}
